import type { FormDetailResponse } from "@/types/entities/formality-request";
import type { Fields } from "../form-correction-table";

type Comment = { text: string; creator: { name: string; last_name: string } };

export type FilledField = {
  id: string;
  value: string;
  label?: string;
  name?: string;
  created_at: string;
  updated_at: string;
  field_id: string;
  correction?: {
    comments?: Comment[];
    status: "APPROVED" | "DENIED";
  } | null;
};

export type ChangeItem = {
  oldFieldId: string;
  newFieldId: string;
  oldValue: string | { [x: string]: string };
  newValue: string | { [x: string]: string };
  comment: string | null;
  changedAt: Date;
  author: string | null;
  order: number;
  field_id: string;
};

export function buildFieldChangeHistory(
  field: Fields,
  formAssigment?: FormDetailResponse["form_assignments"][number],
): ChangeItem[] {
  const filledFields = field.filled_fields as unknown as FilledField[];
  if (field.type === "interoperability") {
    return buildInteroperabilityChangeHistory(field, formAssigment);
  }

  if (!Array.isArray(filledFields) || filledFields.length < 2) return [];

  // (Opcional pero recomendado) asegurar orden cronológico
  const sorted = [...filledFields].sort(
    (a, b) =>
      new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
  );

  const changes: ChangeItem[] = [];

  for (let i = 0; i < sorted.length - 1; i++) {
    const oldFF = sorted[i];
    const newFF = sorted[i + 1];

    if (oldFF.correction && oldFF.correction.status == "DENIED") {
      const comment = oldFF.correction?.comments?.[0];

      changes.push({
        order: i + 1,
        oldFieldId: oldFF.id,
        newFieldId: newFF.id,
        field_id: oldFF.id,
        oldValue: Array.isArray(oldFF.value)
          ? oldFF.value
          : (oldFF.value ?? null),
        newValue: Array.isArray(newFF.value)
          ? newFF.value
          : (newFF.value ?? null),
        comment: comment?.text ?? null,
        changedAt: new Date(newFF.created_at),
        author:
          `${comment?.creator?.name || "Usuario desconocido"} ${comment?.creator?.last_name || ""}`.trim(),
      });
    }
  }

  return changes.sort((a, b) => b.order - a.order);
}

const buildInteroperabilityChangeHistory = (
  field: Fields,
  formAssigment?: FormDetailResponse["form_assignments"][number],
): ChangeItem[] => {
  const filledFields = field.filled_fields as unknown as FilledField[];
  console.log("filledFields", filledFields);

  // for interoperability, if the interoperability has more 2 or more inputs, then there will be 2 or more filledFields representing each input,
  // so if has 2 inputs, there will be 2 filledFields, if has 3 inputs, there will be 3 filledFields, and so on.
  // will need to change the logic to compare the filledFields in pairs, so if has 2 inputs, will compare the first with the second, if has 3 inputs, will compare the first with the second, then the second with the third, and so on.
  const inputCount =
    (field.config as unknown as { input_field?: unknown[] })?.input_field
      ?.length || 1;

  if (!Array.isArray(filledFields) || filledFields.length < inputCount * 2)
    return [];

  // Group filledFields into chunks of inputCount — each chunk is one form submission
  const groups: FilledField[][] = [];
  for (let i = 0; i + inputCount <= filledFields.length; i += inputCount) {
    groups.push(filledFields.slice(i, i + inputCount));
  }

  const changes: ChangeItem[] = [];
  for (let i = 0; i < groups.length - 1; i++) {
    const oldGroup = groups[i];
    const newGroup = groups[i + 1];
    const comment = formAssigment?.revisions
      .flatMap((r) => r.corrections)
      .flatMap((c) => c.field_corrections)
      .find(
        (fc) =>
          fc.filled_field_id === oldGroup[0].id ||
          fc.filled_field_id === newGroup[0].id,
      )?.comments?.[0];
    const oldValue = oldGroup.reduce(
      (acc, ff) => {
        acc[ff.name?.split("__")?.[1] || ff.field_id] = ff.value;
        return acc;
      },
      {} as { [x: string]: string },
    );
    const newValue = newGroup.reduce(
      (acc, ff) => {
        acc[ff.name?.split("__")?.[1] || ff.field_id] = ff.value;
        return acc;
      },
      {} as { [x: string]: string },
    );
    changes.push({
      order: i + 1,
      oldFieldId: oldGroup?.[0].id, // assuming the first filledField in the group represents the whole interoperability field
      newFieldId: newGroup?.[0].id,
      field_id: oldGroup?.[0]?.field_id || "",
      oldValue: oldValue,
      newValue: newValue,
      comment: comment?.text ?? null,
      changedAt: new Date(newGroup?.[0]?.created_at),
      author:
        `${comment?.creator?.name || "Usuario desconocido"} ${comment?.creator?.last_name || ""}`.trim(),
    });
  }

  return changes.sort((a, b) => b.order - a.order);
};
